import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {Box, Button, Stack, TextField, Typography} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {StudyContent} from '@cp/model';
import useImageUpload from '../../../hooks/helper/useImageUpload';
import useVideoUpload from '../../../hooks/helper/useVideoUpload';
import AdminStudyEditorNavigation from './AdminStudyEditorNavigation';

export interface AdminStudyEditorStudyContentProps {
  sections?: StudyContent[];
  onContentChange: (index: number, content: StudyContent[]) => void;
  currentSectionIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  onJumpToSection: (index: number) => void;
  isFirstPage: boolean;
  isLastPage: boolean;
  moduleId: string;
  topic: string;
}

export default function AdminStudyEditorStudyContent({
  sections = [],
  onContentChange,
  currentSectionIndex,
  onPrevious,
  onNext,
  onJumpToSection,
  isFirstPage,
  isLastPage,
  moduleId,
  topic,
}: AdminStudyEditorStudyContentProps) {
  const currentSection = sections[currentSectionIndex];
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleChange = (newTitle?: string, newContent?: string) => {
    if (!currentSection) return;
    const updatedSections = [...sections];
    updatedSections[currentSectionIndex] = {
      title: newTitle || currentSection.title,
      content: newContent || currentSection.content,
    };
    onContentChange(currentSectionIndex, updatedSections);
  };

  const insertTextAtCursor = (text: string) => {
    const textarea = textFieldRef.current;
    if (!textarea || !currentSection) return;

    const currentContent = currentSection.content || '';
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const newContent = currentContent.substring(0, start) + text + currentContent.substring(end);

    handleChange(undefined, newContent);

    // Set cursor position after inserted text
    setTimeout(() => {
      const newPosition = start + text.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);
  };

  const {uploadImage, handleFileSelect, uploading, error} = useImageUpload({
    moduleId,
    topic,
    onUploadComplete: (url) => {
      setUploadError(null);
      const imageMarkdown = `![Image](${url})`;
      insertTextAtCursor(imageMarkdown);
    },
    onError: (errorMsg) => {
      setUploadError(errorMsg);
    },
  });

  const {
    uploadVideo,
    uploadMultipleVideos,
    handleFileSelect: handleVideoFileSelect,
    handleYouTubeLink,
    handleRumbleLink,
    uploading: uploadingVideo,
    error: videoError,
  } = useVideoUpload({
    moduleId,
    topic,
    onUploadComplete: (markdown) => {
      setUploadError(null);
      insertTextAtCursor(markdown);
    },
    onError: (errorMsg) => {
      setUploadError(errorMsg);
    },
  });

  const handleImageButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        uploadImage(target.files[0]);
      }
    };
    input.click();
  };

  const handleVideoButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.multiple = true; // Allow multiple file selection
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        // If multiple files selected, use multiple upload
        if (target.files.length > 1) {
          uploadMultipleVideos(Array.from(target.files));
        } else {
          uploadVideo(target.files[0]);
        }
      }
    };
    input.click();
  };

  // Handle paste events for YouTube and Rumble links
  useEffect(() => {
    const handlePaste = (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      const text = clipboardEvent.clipboardData?.getData('text');
      if (text) {
        // Check if pasted text is a YouTube URL (regular videos, Shorts, or embeds)
        const youtubePattern = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&\n?#\/]+)/;
        if (youtubePattern.test(text)) {
          clipboardEvent.preventDefault();
          handleYouTubeLink(text);
          return;
        }
        // Check if pasted text is a Rumble URL
        const rumblePattern = /rumble\.com\/(?:embed\/|v)([^&\n?#\/]+)/;
        if (rumblePattern.test(text)) {
          clipboardEvent.preventDefault();
          void handleRumbleLink(text);
        }
      }
    };

    const textarea = textFieldRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handlePaste);
      return () => {
        textarea.removeEventListener('paste', handlePaste);
      };
    }
  }, [handleYouTubeLink, handleRumbleLink]);

  return (
    <>
      {sections.length > 0 && (
        <AdminStudyEditorNavigation
          sections={sections}
          currentSectionIndex={currentSectionIndex}
          onPrevious={onPrevious}
          onNext={onNext}
          onJumpToSection={onJumpToSection}
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
        />
      )}
      <Box
        sx={{
          height: '100%',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mt: 2,
        }}
      >
        {currentSection && (
          <TextField
            label="Section Title"
            value={currentSection.title}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter section title..."
            sx={{mb: 2, flexShrink: 0}}
          />
        )}
        <Stack direction="row" spacing={1} sx={{mb: 1}}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={handleImageButtonClick}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VideoFileIcon />}
            onClick={handleVideoButtonClick}
            disabled={uploadingVideo}
          >
            {uploadingVideo ? 'Uploading...' : 'Upload Video'}
          </Button>
          {(error || videoError || uploadError) && (
            <Typography variant="caption" color="error" sx={{alignSelf: 'center'}}>
              {error || videoError || uploadError}
            </Typography>
          )}
        </Stack>
        <TextField
          inputRef={textFieldRef}
          multiline
          fullWidth
          value={currentSection?.content || ''}
          onChange={(e) => handleChange(undefined, e.target.value)}
          placeholder="Enter study content in markdown format..."
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              height: '100%',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              height: '100% !important',
              overflow: 'auto !important',
            },
          }}
        />
        <input type="file" accept="image/*" onChange={handleFileSelect} style={{display: 'none'}} />
        <input type="file" accept="video/*" onChange={handleVideoFileSelect} style={{display: 'none'}} />
      </Box>
    </>
  );
}
